(() => {
  if (customElements.get('fmd-card-purchase')) return;
  let serial = 0;
  class CardPurchase extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.querySelector('form');
      this.variant = this.querySelector('[name="id"]');
      this.add = this.querySelector('[name="add"]');
      this.wallet = this.querySelector('[data-card-wallet]');
      this.notice = this.querySelector('[data-preorder-notice]');
      this.status = this.querySelector('[data-card-status]');
      this.error = this.querySelector('[data-card-error]');
      this.cartLink = this.querySelector('[data-card-cart-link]');
      this.enquiry = this.querySelector('[data-card-enquiry]');
      this.enquiryTrigger = this.enquiry?.querySelector('[data-fmd-enquire]');
      if (!this.form || !this.variant || !this.add) return;
      this.ready = true;
      const id = `fmd-card-purchase-${++serial}`;
      this.form.id = id;
      if (this.variant.tagName === 'SELECT') {
        this.variant.id = `${id}-variant`;
        this.querySelector('label').htmlFor = this.variant.id;
      }
      this.form.addEventListener('change', () => {
        if (!this.busy) {
          this.status.textContent = '';
          this.error.hidden = true;
          this.cartLink.hidden = true;
        }
        this.update();
      });
      this.form.addEventListener('submit', (event) => this.submit(event));
      // A colour swatch changes product links, not this exact-variant selection.
      this.closest('product-card')?.addEventListener('change', (event) => {
        if (event.target.matches('.opt-btn') && this.variant.tagName === 'SELECT') {
          this.variant.value = '';
          this.update();
        }
      });
      this.update();
      this.hidden = false;
      if (this.nextElementSibling?.hasAttribute('data-card-fallback')) this.nextElementSibling.hidden = true;
    }
    selection() {
      return this.variant.tagName === 'SELECT' ? this.variant.selectedOptions[0] : this.variant;
    }
    update() {
      const selected = this.selection();
      const available = Boolean(this.variant.value && selected?.dataset.available === 'true');
      const preorder = selected?.dataset.preorder === 'true';
      const enquire = Boolean(this.variant.value && !available && !this.busy);
      this.add.disabled = !available || this.busy;
      this.add.hidden = enquire;
      this.add.textContent = this.busy ? 'Adding…' : preorder ? 'Preorder' : 'Add to cart';
      this.wallet.hidden = !available || this.busy;
      this.notice.hidden = !available || !preorder;
      if (this.enquiry) this.enquiry.hidden = !enquire;
      if (enquire && this.enquiryTrigger) {
        const title = selected.dataset.title || 'Default Title';
        const productURL = new URL(this.dataset.productUrl, location.origin);
        productURL.searchParams.set('variant', this.variant.value);
        Object.assign(this.enquiryTrigger.dataset, {
          variantId: this.variant.value, variantTitle: title,
          sku: selected.dataset.sku || '', productUrl: productURL.href
        });
        const name = `${this.dataset.productTitle}${title === 'Default Title' ? '' : ` — ${title}`}`;
        this.enquiryTrigger.textContent = 'Notify me';
        this.enquiryTrigger.setAttribute('aria-label', `Notify me: ${name}`);
        const mail = new URL(this.enquiryTrigger.href);
        mail.searchParams.set('subject', `Availability enquiry: ${this.dataset.productTitle}`);
        mail.searchParams.set('body', `Please check availability for ${name}. Product: ${productURL.href}`);
        this.enquiryTrigger.href = mail.href;
      }
      // The native id field and bubbling change event are also read by Shopify's wallet.
    }
    async submit(event) {
      // Leave native accelerated-checkout events to Shopify.
      if (event.submitter && event.submitter !== this.add) return;
      event.preventDefault();
      if (this.busy || !this.variant.value || this.selection()?.dataset.available !== 'true') return;
      if (!this.form.reportValidity()) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.error.hidden = true;
      this.status.textContent = '';
      this.cartLink.hidden = true;
      const payload = new FormData(this.form);
      const drawer = document.querySelector('cart-drawer');
      const drawerSection = drawer?.closest('.shopify-section')?.id.replace('shopify-section-', '');
      const sectionNames = ['cart-icon-bubble', drawerSection].filter(Boolean);
      payload.set('sections', sectionNames.join(','));
      payload.set('sections_url', location.pathname);
      // Freeze selection after constructing FormData, preserving the submitted variant.
      this.variant.disabled = true;
      this.update();
      let accepted = false;
      try {
        const response = await fetch(window.theme?.routes.cartAdd || `${window.Shopify?.routes.root || '/'}cart/add.js`, {
          method: 'POST', headers: { Accept: 'application/json' }, body: payload
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          const message = typeof data.description === 'string' ? data.description : 'This option could not be added. Please check availability and try again.';
          this.error.textContent = message;
          this.error.hidden = false;
          return;
        }
        accepted = true;
        this.status.textContent = 'Added to cart.';
        this.cartLink.hidden = false;
        this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: data.variant_id } }));
        const countHTML = data.sections?.['cart-icon-bubble'];
        const count = document.getElementById('cart-icon-bubble');
        if (count && typeof countHTML === 'string') {
          const section = new DOMParser().parseFromString(countHTML, 'text/html');
          count.innerHTML = section.getElementById('cart-icon-bubble')?.innerHTML || countHTML;
        }
        if (drawer && drawerSection && typeof data.sections?.[drawerSection] === 'string') {
          await drawer.renderContents(data, window.theme?.settings.afterAtc === 'drawer');
        }
      } catch (error) {
        // Never automatically retry a POST: a lost response may still have added the item.
        this.error.textContent = accepted ? 'Your item was added. Open your cart to continue.' : 'We could not confirm the cart update. Check your cart before trying again.';
        this.error.hidden = false;
        this.cartLink.hidden = false;
      } finally {
        this.busy = false;
        this.variant.disabled = false;
        this.removeAttribute('aria-busy');
        this.update();
      }
    }
  }
  customElements.define('fmd-card-purchase', CardPurchase);

  // Equalise only cards sharing a visual row. Never truncate copy or invent
  // wallet buttons for unavailable products. Filters/sliders may replace cards.
  let layoutFrame = 0;
  const scheduleLayout = () => {
    if (!layoutFrame) layoutFrame = requestAnimationFrame(alignRows);
  };
  const alignRows = () => {
    layoutFrame = 0;
    const entries = Array.from(document.querySelectorAll('product-card')).map(card => {
      const info = card.querySelector('.card__info');
      const group = Array.from(card.querySelector('.card__info-container')?.children || [])
        .find(el => el.matches('.fmd-card-purchase, .fmd-enquiry-card') && !el.hidden);
      const action = group?.querySelector('[name="add"]:not([hidden]), [data-fmd-enquire]:not([hidden])')
        || (group?.matches('a') ? group : group?.querySelector('a.btn'));
      return { card, info, group, action, vendor: card.querySelector('.card__vendor'), title: card.querySelector('.card__title'), price: card.querySelector('.price') };
    }).filter(e => e.info && e.group && e.action && e.card.getClientRects().length);
    // Remove our sizing first so narrow/wide changes can shrink as well as grow.
    for (const e of entries) {
      for (const el of [e.vendor, e.title, e.info]) el?.style.removeProperty('min-height');
      e.group.style.removeProperty('padding-top');
      e.price?.style.removeProperty('margin-top');
    }
    const rows = [];
    for (const e of entries) {
      if (e.card.closest('.main-products-grid[data-layout="list"]')) continue;
      const top = e.card.getBoundingClientRect().top;
      let row = rows.find(r => r.parent === e.card.parentElement.parentElement && Math.abs(r.top - top) < 2);
      if (!row) { row = { parent: e.card.parentElement.parentElement, top, entries: [] }; rows.push(row); }
      row.entries.push(e);
    }
    const sharedRows = rows.filter(row => row.entries.length > 1);
    const textHeights = sharedRows.map(row => ({ row,
      vendor: Math.max(...row.entries.map(e => e.vendor?.getBoundingClientRect().height || 0)),
      title: Math.max(...row.entries.map(e => e.title?.getBoundingClientRect().height || 0))
    }));
    for (const { row, vendor, title } of textHeights) for (const e of row.entries) {
      if (e.vendor) e.vendor.style.minHeight = `${vendor}px`;
      if (e.title) e.title.style.minHeight = `${title}px`;
    }
    // Optional ratings/subtitles/weight also precede the price in this theme.
    const priceRows = sharedRows.map(row => row.entries.filter(e => e.price).map(e => ({
      price: e.price, top: e.price.getBoundingClientRect().top,
      margin: parseFloat(getComputedStyle(e.price).marginTop) || 0
    })));
    for (const prices of priceRows) {
      const top = Math.max(...prices.map(p => p.top));
      for (const p of prices) p.price.style.marginTop = `${p.margin + top - p.top}px`;
    }
    const infoHeights = sharedRows.map(row => ({ row, height: Math.max(...row.entries.map(e => e.info.getBoundingClientRect().height)) }));
    for (const { row, height } of infoHeights) for (const e of row.entries) e.info.style.minHeight = `${height}px`;
    const leads = sharedRows.map(row => ({ row, values: row.entries.map(e => Math.max(0, e.action.getBoundingClientRect().top - e.group.getBoundingClientRect().top)) }));
    for (const { row, values } of leads) {
      const lead = Math.max(...values);
      row.entries.forEach((e, i) => { e.group.style.paddingTop = `${lead - values[i]}px`; });
    }
  };
  // Wallet internals sit below the aligned action. Their loading/animation
  // changes must not repeatedly measure every card on the page.
  const affectsCardLayout = node => {
    const element = node?.nodeType === 1 ? node : node?.parentElement;
    if (!element || element.closest('[data-card-wallet], .shopify-payment-button')) return false;
    return Boolean(element.closest('product-card') || element.querySelector('product-card'));
  };
  // Preserve card replacement, filtering, ancestor visibility and list/grid
  // changes; ignore unrelated hero, app and other page-content mutations.
  const observer = new MutationObserver(records => {
    if (records.some(record => affectsCardLayout(record.target)
      || (record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].some(affectsCardLayout)))) {
      scheduleLayout();
    }
  });
  observer.observe(document.querySelector('main') || document.body, {
    childList: true, subtree: true, characterData: true,
    attributes: true, attributeFilter: ['hidden', 'class', 'data-layout']
  });
  window.addEventListener('resize', scheduleLayout, { passive: true });
  const onCardEvent = event => { if (affectsCardLayout(event.target)) scheduleLayout(); };
  document.addEventListener('change', onCardEvent);
  document.addEventListener('load', onCardEvent, true);
  document.fonts?.ready.then(scheduleLayout);
  scheduleLayout();
})();
